






























































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Model, Prop, Watch } from 'vue-property-decorator';
import TextInput from '@components/020-TextInput/TextInput.vue';
import SelectInput from '@components/021-SelectInput/SelectInput.vue';
import ISelectOption from '@frontend/types/base/ISelectOption';

interface IValueSet {
	sfLocation: string;
	sfJobtype: string;
	sfAttributes: string;
	sfLocationLabel: string;
	sfJobtypeLabel: string;
	sfAttributesLabel: string;
}

enum Field {
	SFFIELD01 = 'sfLocation',
	SFFIELD02 = 'sfJobtype',
	SFFIELD03 = 'sfAttributes',
	SFFIELD01LABEL = 'sfLocationLabel', // this needs to be set to SFFIELD01 + 'Label'
	SFFIELD02LABEL = 'sfJobtypeLabel', // this needs to be set to SFFIELD01 + 'Label'
	SFFIELD03LABEL = 'sfAttributesLabel' // this needs to be set to SFFIELD01 + 'Label'
}

@Component({
	components: {
		SelectInput,
		TextInput
	}
})
export default class JobSelection extends Vue {
	// Provide enum to template
	protected Field: any = Field;

	@Model('change', { type: Object, default: () => ({ sfLocation: '', sfJobtype: '', sfAttributes: '' }) })
	protected readonly value!: IValueSet;

	@Prop(String)
	protected readonly id!: string;

	@Prop({ type: String, default: '' })
	protected readonly name!: string;

	@Prop(String)
	protected readonly label?: string;

	@Prop({ type: String, default: 'text' })
	protected readonly type!: string;

	@Prop({ type: Array })
	protected readonly optionSets!: IValueSet[];

	@Prop({ type: Object })
	protected readonly jobSelectionFieldConfig!: Record<string, string>;

	@Prop({ default: null })
	protected readonly sfJobSelectionOptionsSingleJobReqId?: string | number | null;

	@Prop({ type: Object, default: null })
	protected readonly error!: string | Record<string, string> | null;

	protected get sfLocationOptions(): ISelectOption[] {
		const options: string[] = this.optionSets.map((optionSet: IValueSet): string => optionSet.sfLocation);
		const labels: string[] = this.optionSets.map((labelSet: IValueSet): string => labelSet.sfLocationLabel);
		const uniqueOptionsWithLabels = options
			.map((option, index) => ({ value: option, label: labels[index] })) // Pair each option with its label
			.filter(
				(item, index, self) => self.findIndex(({ value }) => value === item.value) === index // Filter unique options
			);
		return uniqueOptionsWithLabels
			.map(({ value, label }) => ({
				label: label || value,
				value
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	}

	protected get sfJobtypeOptions(): ISelectOption[] {
		const options: string[] = this.optionSets
			.filter((optionSet: IValueSet): boolean => !!this.value && optionSet.sfLocation === this.sfLocation)
			.map((optionSet: IValueSet): string => optionSet.sfJobtype);
		const labels: string[] = this.optionSets
			.filter((optionSet: IValueSet): boolean => !!this.value && optionSet.sfLocation === this.sfLocation)
			.map((optionSet: IValueSet): string => optionSet.sfJobtypeLabel);
		const uniqueOptionsWithLabels = options
			.map((option, index) => ({ value: option, label: labels[index] })) // Pair each option with its label
			.filter(
				(item, index, self) => self.findIndex(({ value }) => value === item.value) === index // Filter unique options
			);
		return uniqueOptionsWithLabels
			.map(({ value, label }) => ({
				label: label || value,
				value
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	}

	protected get sfAttributesOptions(): ISelectOption[] {
		const options: string[] = this.optionSets
			.filter(
				(optionSet: IValueSet): boolean =>
					!!this.value && optionSet.sfLocation === this.sfLocation && optionSet.sfJobtype === this.sfJobtype
			)
			.map((optionSet: IValueSet): string => optionSet.sfAttributes);
		const labels: string[] = this.optionSets
			.filter(
				(optionSet: IValueSet): boolean =>
					!!this.value && optionSet.sfLocation === this.sfLocation && optionSet.sfJobtype === this.sfJobtype
			)
			.map((optionSet: IValueSet): string => optionSet.sfAttributesLabel);
		const uniqueOptionsWithLabels = options
			.map((option, index) => ({ value: option, label: labels[index] })) // Pair each option with its label
			.filter(
				(item, index, self) => self.findIndex(({ value }) => value === item.value) === index // Filter unique options
			);
		return uniqueOptionsWithLabels
			.map(({ value, label }) => ({
				label: label || value,
				value
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	}

	protected get sfJobtypePrompt(): string {
		if (this.sfLocation === '') return this.i18n.pleaseChooseOffice;
		return this.jobSelectionFieldConfig[Field.SFFIELD02];
	}

	protected get sfAttributesPrompt(): string {
		if (this.sfLocation === '') return this.i18n.pleaseChooseOffice;
		if (this.sfJobtype === '') return this.i18n.pleaseChooseJobType;
		return this.jobSelectionFieldConfig[Field.SFFIELD03];
	}

	protected get sfLocation(): string {
		return this.value[Field.SFFIELD01];
	}

	protected set sfLocation(sfLocation: string) {
		this.$emit('change', {
			...this.value,
			[Field.SFFIELD01]: sfLocation
		});
	}

	protected get sfJobtype(): string {
		return this.value[Field.SFFIELD02];
	}

	protected set sfJobtype(sfJobtype: string) {
		this.$emit('change', {
			...this.value,
			[Field.SFFIELD02]: sfJobtype
		});
	}

	protected get sfAttributes(): string {
		return this.value[Field.SFFIELD03];
	}

	protected set sfAttributes(sfAttributes: string) {
		this.$emit('change', {
			...this.value,
			[Field.SFFIELD03]: sfAttributes
		});
	}

	protected get internalErrors(): Record<Field, string | null> {
		if (!this.error || typeof this.error === 'string') {
			// In case the error is a string, it will be handled in FormInput for the input as a whole
			return {
				[Field.SFFIELD01]: null,
				[Field.SFFIELD01LABEL]: null,
				[Field.SFFIELD02]: null,
				[Field.SFFIELD02LABEL]: null,
				[Field.SFFIELD03]: null,
				[Field.SFFIELD03LABEL]: null
			};
		}

		return this.error;
	}

	protected onInvalid(field: string, event: Event): void {
		if (this.sfJobSelectionOptionsSingleJobReqId) {
			return;
		}
		const input: HTMLInputElement = event.currentTarget as HTMLInputElement;

		this.$emit('invalid', {
			...this.internalErrors,
			[field]: input.validationMessage
		});
	}

	@Watch('sfLocation')
	protected onOfficeChanged(): void {
		if (!this.sfJobtypeOptions.some((option: ISelectOption): boolean => option.value === this.sfJobtype)) {
			// Selected sfJobtype not found in the newly selected sfLocation
			this.sfJobtype = '';
		} else if (!this.sfAttributesOptions.some((option: ISelectOption): boolean => option.value === this.sfAttributes)) {
			// Selected sfAttributes not found in the newly selected sfLocation
			this.sfAttributes = '';
		}
	}

	@Watch('sfJobtype')
	protected onJobTypeChanged(): void {
		if (!this.sfAttributesOptions.some((option: ISelectOption): boolean => option.value === this.sfAttributes)) {
			// Selected sfAttributes not found in the newly selected sfJobtype
			this.sfAttributes = '';
		}
	}
}
